import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQuantity, type DiaryEntry, type MealGroup } from '@nutrigeist/shared';
import { diaryApi } from '../api/endpoints.js';
import { useCurrentUser } from '../auth/AuthContext.js';
import { MacroSummary } from '../components/MacroSummary.js';
import { NutrientDetails } from '../components/NutrientDetails.js';
import { EatingWindow } from '../components/EatingWindow.js';
import { WaterCard } from '../components/WaterCard.js';
import { ActivityCard } from '../components/ActivityCard.js';
import { LogFoodSheet, type LoggableFood } from '../components/LogFoodSheet.js';
import {
  ChartIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CopyIcon,
  TrashIcon,
} from '../components/icons.js';
import {
  MEAL_LABELS,
  addDays,
  formatDateLabel,
  formatKcal,
  formatTime,
  todayInTimeZone,
} from '../lib/format.js';

export function TodayPage(): JSX.Element {
  const user = useCurrentUser();
  const timeZone = user.preferences.timezone;
  const queryClient = useQueryClient();

  const today = todayInTimeZone(timeZone);
  const [date, setDate] = useState(() => today);
  const [editing, setEditing] = useState<DiaryEntry | null>(null);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const isToday = date === today;

  const dayQuery = useQuery({
    queryKey: ['diary', 'day', date],
    queryFn: () => diaryApi.day({ date, timezone: timeZone }),
  });

  const deleteEntry = useMutation({
    mutationFn: (id: string) => diaryApi.deleteEntry(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['diary'] }),
  });

  const copyEntries = useMutation({
    mutationFn: (input: { entryIds: string[]; label: string }) =>
      diaryApi.copy({ entryIds: input.entryIds, date: today, timezone: timeZone }),
    onSuccess: async (result, input) => {
      await queryClient.invalidateQueries({ queryKey: ['diary'] });
      // Jump to today so the freshly copied entries are what the user sees.
      setDate(today);
      setCopyNote(`${input.label} nach Heute kopiert (${result.entries.length}).`);
    },
  });

  return (
    <div className="stack">
      <div className="page-header">
        <div>
          <h1>{formatDateLabel(date, timeZone)}</h1>
          <span className="subtitle">{user.displayName}</span>
        </div>

        <div className="row">
          <Link to="/trends" className="icon-btn" aria-label="Verlauf">
            <ChartIcon />
          </Link>
          <button
            className="icon-btn"
            aria-label="Vorheriger Tag"
            onClick={() => setDate((current) => addDays(current, -1))}
          >
            <ChevronLeftIcon />
          </button>
          <button
            className="icon-btn"
            aria-label="Nächster Tag"
            disabled={isToday}
            style={{ opacity: isToday ? 0.35 : 1 }}
            onClick={() => setDate((current) => addDays(current, 1))}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      {copyNote && (
        <div className="banner banner-info" role="status">
          {copyNote}
        </div>
      )}

      {dayQuery.isPending && (
        <div className="empty-state">
          <div className="spinner" style={{ margin: '0 auto' }} role="status" aria-label="Lädt" />
        </div>
      )}

      {dayQuery.isError && (
        <div className="banner banner-error" role="alert">
          Tagesdaten konnten nicht geladen werden.
        </div>
      )}

      {dayQuery.data && (
        <>
          <MacroSummary summary={dayQuery.data} />

          {dayQuery.data.entryCount > 0 && (
            <NutrientDetails
              totals={dayQuery.data.totals}
              fiberGoal={dayQuery.data.goals.fiber}
              fiberGaps={
                dayQuery.data.meals
                  .flatMap((meal) => meal.entries)
                  // A food with energy but no fibre row almost always means the
                  // value was never declared, not that it is genuinely zero.
                  .filter((entry) => entry.per100.fiber === 0 && entry.per100.kcal > 0).length
              }
            />
          )}

          {dayQuery.data.entryCount > 0 && (
            <EatingWindow
              date={date}
              timeZone={timeZone}
              entries={dayQuery.data.meals.flatMap((meal) => meal.entries)}
            />
          )}

          <WaterCard
            date={date}
            timeZone={timeZone}
            goalMl={user.goals.waterMl}
            editable={isToday}
          />

          <ActivityCard date={date} timeZone={timeZone} intakeKcal={dayQuery.data.totals.kcal} />

          {/* Copying a past day wholesale is the fast path for repeated eating
              patterns; only offered when there is something to copy and the day
              is not already today. */}
          {!isToday && dayQuery.data.entryCount > 0 && (
            <button
              className="btn btn-ghost btn-block"
              disabled={copyEntries.isPending}
              onClick={() =>
                copyEntries.mutate({
                  entryIds: dayQuery.data.meals.flatMap((meal) =>
                    meal.entries.map((entry) => entry.id),
                  ),
                  label: 'Ganzer Tag',
                })
              }
            >
              <CopyIcon />
              Diesen Tag nach Heute kopieren
            </button>
          )}

          {dayQuery.data.entryCount === 0 ? (
            <div className="card empty-state">
              Noch nichts erfasst. Tippe auf <strong>Hinzufügen</strong>, um zu starten.
            </div>
          ) : (
            dayQuery.data.meals
              .filter((meal) => meal.entries.length > 0)
              .map((meal) => (
                <MealCard
                  key={meal.mealType}
                  meal={meal}
                  timeZone={timeZone}
                  showCopy={!isToday}
                  copying={copyEntries.isPending}
                  onCopy={() =>
                    copyEntries.mutate({
                      entryIds: meal.entries.map((entry) => entry.id),
                      label: MEAL_LABELS[meal.mealType] ?? meal.mealType,
                    })
                  }
                  onEdit={setEditing}
                  onDelete={(id) => deleteEntry.mutate(id)}
                  deleting={deleteEntry.isPending}
                />
              ))
          )}
        </>
      )}

      {editing && <EditEntrySheet entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

interface MealCardProps {
  meal: MealGroup;
  timeZone: string;
  showCopy: boolean;
  copying: boolean;
  onCopy: () => void;
  onEdit: (entry: DiaryEntry) => void;
  onDelete: (id: string) => void;
  deleting: boolean;
}

function MealCard({
  meal,
  timeZone,
  showCopy,
  copying,
  onCopy,
  onEdit,
  onDelete,
  deleting,
}: MealCardProps): JSX.Element {
  return (
    <div className="card meal-group">
      <div className="meal-heading">
        <span>{MEAL_LABELS[meal.mealType] ?? meal.mealType}</span>
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          <span>{formatKcal(meal.totals.kcal)} kcal</span>
          {showCopy && (
            <button
              className="icon-btn"
              aria-label={`${MEAL_LABELS[meal.mealType] ?? meal.mealType} nach Heute kopieren`}
              disabled={copying}
              onClick={onCopy}
            >
              <CopyIcon />
            </button>
          )}
        </div>
      </div>

      <div className="entry-list">
        {meal.entries.map((entry) => (
          <div className="entry" key={entry.id}>
            {/* Tapping the row opens the edit sheet — the quantity is the field
                most often gotten wrong at logging time. */}
            <button className="entry-tap" onClick={() => onEdit(entry)}>
              <div className="entry-name">{entry.name}</div>
              <div className="entry-meta">
                {formatQuantity(entry.quantity)}
                {entry.brand ? ` · ${entry.brand}` : ''} · {formatTime(entry.loggedAt, timeZone)}
              </div>
            </button>

            <span className="entry-kcal">{formatKcal(entry.totals.kcal)} kcal</span>

            <button
              className="icon-btn"
              aria-label={`${entry.name} löschen`}
              disabled={deleting}
              onClick={() => onDelete(entry.id)}
            >
              <TrashIcon />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Rebuilds a loggable food from an existing entry so the edit sheet can reuse
 * the same amount/unit machinery and live preview.
 *
 * Nutrients come from the entry's own snapshot, and the serving weight / density
 * are inverted from its original measurement — the same recovery the API does —
 * so an ad-hoc "2 Stück" entry stays editable in pieces even with no catalog
 * food behind it.
 */
function entryAsLoggable(entry: DiaryEntry): LoggableFood {
  const { amount, unit } = entry.quantity;
  const usable = amount > 0 && entry.resolvedGrams > 0;

  const serving =
    usable && (unit === 'piece' || unit === 'serving')
      ? { grams: entry.resolvedGrams / amount, label: '1 Portion' }
      : null;

  let density: number | null = null;
  if (usable && unit === 'ml' && entry.basis === 'per_100g') density = entry.resolvedGrams / amount;
  else if (usable && unit === 'g' && entry.basis === 'per_100ml')
    density = amount / entry.resolvedGrams;

  return {
    id: entry.foodId,
    name: entry.name,
    brand: entry.brand,
    basis: entry.basis,
    nutrients: entry.per100,
    serving,
    density,
  };
}

function EditEntrySheet({
  entry,
  onClose,
}: {
  entry: DiaryEntry;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();

  return (
    <LogFoodSheet
      food={entryAsLoggable(entry)}
      initialQuantity={entry.quantity}
      initialMealType={entry.mealType}
      submitLabel="Speichern"
      onClose={onClose}
      onLogged={onClose}
      onSubmit={async (_food, quantity, mealType) => {
        await diaryApi.updateEntry(entry.id, { quantity, mealType });
        await queryClient.invalidateQueries({ queryKey: ['diary'] });
      }}
    />
  );
}
